import type { Ref } from "lit/directives/ref.js";
import {
  arrow,
  computePosition,
  flip,
  offset,
  shift,
  type Placement,
  type Strategy,
} from "@floating-ui/dom";
import { html, nothing, LitElement, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "@/common/directives/when";
import { EkoElement } from "@/utilities/element";
import { createSlotChangeHandler, setInitialSlotProperties } from "@/utilities/slot";
import style from "./tooltip.css?inline";

@customElement("eko-tooltip")
class Tooltip extends EkoElement {
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
  static slotPropertyMap = {
    supporting: "supportingElement",
  } as const;

  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private arrowRef: Ref<HTMLElement> = createRef();

  private handleSlotChange = createSlotChangeHandler(this, Tooltip.slotPropertyMap);
  private resizeObserver: ResizeObserver | null = null;

  private setPosition = () => {
    const triggerElement = this.triggerRef.value!;
    const tooltipElement = this.tooltipRef.value!;

    const middleware = [offset(this.arrow ? 10 : 6), flip(), shift({ padding: 5 })];
    if (this.arrow && this.arrowRef.value) {
      middleware.push(arrow({ element: this.arrowRef.value }));
    }

    computePosition(triggerElement, tooltipElement, {
      middleware,
      placement: this.position as Placement,
      strategy: this.strategy,
    }).then(({ middlewareData, placement, x, y }) => {
      Object.assign(tooltipElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      if (this.arrow && this.arrowRef.value) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow as { x: number; y: number };

        const staticSide = {
          bottom: "top",
          left: "right",
          right: "left",
          top: "bottom",
        }[placement.split("-")[0]] as string;

        Object.assign(this.arrowRef.value.style, {
          bottom: "",
          left: arrowX != null ? `${arrowX}px` : "",
          right: "",
          top: arrowY != null ? `${arrowY}px` : "",
          ...(staticSide && { [staticSide]: "-5px" }),
        });
      }
    });
  };

  private tooltipRef: Ref<HTMLElement> = createRef();
  private triggerRef: Ref<HTMLElement> = createRef();

  @property({ reflect: true, type: Boolean }) arrow = false;
  @property({ reflect: true }) position: Placement = "top";
  @property({ reflect: true }) strategy: Strategy = "absolute"; // 'fixed' strategy will “break” the floating element out of its parent clipping context (not guaranteed).
  @state() supportingElement: string = "";

  private hideTooltip() {
    this.tooltipRef.value!.classList.remove("eko-tooltip__content-active");
  }

  private showTooltip() {
    this.tooltipRef.value!.classList.add("eko-tooltip__content-active");
    this.setPosition();
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.setPosition);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener("resize", this.setPosition);
    super.disconnectedCallback();
  }

  firstUpdated() {
    setInitialSlotProperties(this, Tooltip.slotPropertyMap);
    this.setPosition();
    this.resizeObserver = new ResizeObserver(this.setPosition);
    this.resizeObserver.observe(this.tooltipRef.value!);
  }

  render() {
    return html`
      <slot
        aria-describedby="eko-tooltip"
        ${ref(this.triggerRef)}
        @mouseenter=${this.showTooltip}
        @mouseleave=${this.hideTooltip}
        @touchstart=${this.showTooltip}
        @focusin=${this.showTooltip}
        @focusout=${this.hideTooltip}
        class="eko-tooltip__trigger"></slot>
      <div
        id="eko-tooltip"
        class="eko-tooltip__content"
        role="tooltip"
        ${ref(this.tooltipRef)}
        part="content">
        <div class="eko-tooltip__content-container" part="content-container">
          <slot name="content" class="eko-tooltip__content-text"></slot>
          <slot
            name="supporting"
            class=${when(this.supportingElement, "eko-tooltip__content-supporting")}
            @slotchange=${this.handleSlotChange}></slot>
        </div>
        <slot class="eko-tooltip__content-shortcut" name="shortcut"></slot>
        ${this.arrow
          ? html`<div class="eko-tooltip__arrow" ${ref(this.arrowRef)} part="arrow"></div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-tooltip": Tooltip;
  }
}

export default Tooltip;
